/**
 * DimensionData — Discriminated Union pro Tragfähigkeits-Dimension.
 *
 * Der Diskriminator ist `dimension` (interner Code-Schlüssel). Die externen
 * Begriffe (für Mandant/Jobcenter-Sichtbares) werden in der UI-Schicht
 * zugeordnet, damit Methodik und Begrifflichkeit unabhängig versionierbar
 * sind.
 *
 * Pro Dimension werden Pflicht- und Optional-Felder unterschieden — die
 * Konfidenzberechnung in `scoring/computeConfidence.ts` zählt pro Dimension
 * den Anteil ausgefüllter Pflichtfelder.
 *
 * Für die Score-Heuristiken sind Roh-Strukturdaten relevant; die Score-
 * Engine bekommt diese Schemas als Input.
 */
import { z } from 'zod';
import { HoursSchema } from './brands.js';
import { CaseIdSchema, DimensionDataIdSchema, FinancialDatasetIdSchema } from './ids.js';

export const DimensionKeySchema = z.enum([
  'identity',
  'activity',
  'style',
  'resources',
  'health',
  'private',
]);
export type DimensionKey = z.infer<typeof DimensionKeySchema>;

// ----------------------------------------------------------------------------
// 1 — Identity & Qualification
// ----------------------------------------------------------------------------
export const QualificationSchema = z.object({
  type: z.enum(['ausbildung', 'studium', 'meister', 'weiterbildung', 'sonstige']),
  title: z.string().min(1).max(200),
  yearObtained: z.number().int().min(1900).max(2100),
  industryRelevant: z.boolean(),
});
export type Qualification = z.infer<typeof QualificationSchema>;

export const PersonalTraitSchema = z.enum([
  'gewissenhaftigkeit',
  'zielorientierung',
  'durchhaltevermoegen',
  'durchsetzungsstaerke',
  'risikotoleranz',
  'akquisestaerke',
  'lernbereitschaft',
]);
export type PersonalTrait = z.infer<typeof PersonalTraitSchema>;

const IdentityDataSchema = z.object({
  dimension: z.literal('identity'),
  qualifications: z.array(QualificationSchema).default([]),
  industryExperienceYears: HoursSchema.optional(), // nominal Jahre, Hours-Brand reused for non-negative number
  hasEntrepreneurialExperience: z.boolean(),
  yearsSelfEmployed: z.number().int().min(0).max(80).optional(),
  usp: z.string().max(2000).optional(), // qualitativer Freitext
  personalTraits: z.array(PersonalTraitSchema).default([]),
  notes: z.string().max(5000).optional(),
});
export type IdentityData = z.infer<typeof IdentityDataSchema>;

// ----------------------------------------------------------------------------
// 2 — Activity & Geschäftsmodell
// ----------------------------------------------------------------------------
const IdAndShareSchema = z.object({
  /** Frei wählbarer ID-/Bezeichner-String, z.B. "Kunde A" */
  id: z.string().min(1).max(100),
  /** Anteil am Gesamtumsatz, 0-100 */
  revenueSharePercent: z.number().min(0).max(100),
});

const ActivityDataSchema = z.object({
  dimension: z.literal('activity'),
  valueProposition: z.string().max(2000).optional(),
  targetCustomers: z.string().max(2000).optional(),
  /** Auftraggeber-Konzentration für Klumpenrisiko-Erkennung */
  topClientsByRevenue: z.array(IdAndShareSchema).default([]),
  hasRecurringRevenue: z.boolean(),
  recurringRevenueSharePercent: z.number().min(0).max(100).optional(),
  scalability: z.enum(['linear', 'partly_scalable', 'highly_scalable', 'unknown']),
  hasDocumentedSalesProcess: z.boolean(),
  notes: z.string().max(5000).optional(),
});
export type ActivityData = z.infer<typeof ActivityDataSchema>;

// ----------------------------------------------------------------------------
// 3 — Style & Marktposition
// ----------------------------------------------------------------------------
const StyleDataSchema = z.object({
  dimension: z.literal('style'),
  targetGroupDefined: z.boolean(),
  targetGroupDescription: z.string().max(2000).optional(),
  competitors: z.array(z.string().min(1).max(200)).default([]),
  differentiators: z.string().max(2000).optional(),
  /** Selbstgesetzter Stundensatz (netto) — Vergleich mit Branche in Score */
  selfReportedHourlyRateEuro: z.number().nonnegative().optional(),
  /** Branchenübliche Untergrenze (kann von Berater erfasst werden) */
  industryFloorHourlyRateEuro: z.number().nonnegative().optional(),
  pricingStrategyDocumented: z.boolean(),
  notes: z.string().max(5000).optional(),
});
export type StyleData = z.infer<typeof StyleDataSchema>;

// ----------------------------------------------------------------------------
// 4 — Resources & Finances (verweist auf FinancialDataset)
// ----------------------------------------------------------------------------
const ResourcesDataSchema = z.object({
  dimension: z.literal('resources'),
  /** Verweis auf das BWA-/EKS-Dataset, das die Hauptdatengrundlage bildet. */
  financialDatasetId: FinancialDatasetIdSchema.optional(),
  /** Zusätzliche qualitative Anmerkungen, die nicht aus Zahlen ableitbar sind */
  liquidityCushionExplanation: z.string().max(2000).optional(),
  outstandingClaimsExplanation: z.string().max(2000).optional(),
  capitalNeedsForecastEuroPerMonth: z.number().optional(),
  notes: z.string().max(5000).optional(),
});
export type ResourcesData = z.infer<typeof ResourcesDataSchema>;

// ----------------------------------------------------------------------------
// 5 — Health & Resilience
// ----------------------------------------------------------------------------
const HealthDataSchema = z.object({
  dimension: z.literal('health'),
  /** Selbsteinschätzung 1-10 (nicht 0-100, weil semantisch eine Likert-Skala) */
  selfReportedResilienceScore: z.number().int().min(1).max(10).optional(),
  hoursWorkedPerWeek: HoursSchema.optional(),
  hasMedicalCertification: z.boolean(),
  chronicConditionsExist: z.boolean(),
  conditionsAffectWork: z.boolean(),
  copingStrategiesDescribed: z.string().max(2000).optional(),
  pastCrisisHandlingNotes: z.string().max(5000).optional(),
  notes: z.string().max(5000).optional(),
});
export type HealthData = z.infer<typeof HealthDataSchema>;

// ----------------------------------------------------------------------------
// 6 — Private Umfeld & Bedarfsgemeinschaft
// ----------------------------------------------------------------------------
/**
 * Kinderbetreuungs-Status. Drei-wertig statt boolean, weil die binäre
 * `false`-Default-Variante immer fälschlich „nicht gesichert" implizierte
 * (und damit Risikoindikator + Score-Abzug auslöste). `not_applicable` ist
 * der Default für BG ohne Kinder.
 */
export const ChildcareStatusSchema = z.enum(['not_applicable', 'secured', 'not_secured']);
export type ChildcareStatus = z.infer<typeof ChildcareStatusSchema>;

const PrivateDataSchema = z.object({
  dimension: z.literal('private'),
  /** Verweis auf das FinancialDataset (enthält BG-Komposition + KdU) */
  financialDatasetId: FinancialDatasetIdSchema.optional(),
  /** Kinderbetreuungs-Status (drei-wertig, siehe `ChildcareStatusSchema`). */
  childcareStatus: ChildcareStatusSchema,
  /** Pflegeverantwortung im Haushalt? */
  hasCareResponsibility: z.boolean(),
  careResponsibilityDescription: z.string().max(2000).optional(),
  /** Zumutbarkeitsgründe nach § 10 Abs. 1 SGB II als Freitext */
  zumutbarkeitsgruende: z.string().max(5000).optional(),
  regionalLaborMarketContext: z.string().max(2000).optional(),
  notes: z.string().max(5000).optional(),
});
export type PrivateData = z.infer<typeof PrivateDataSchema>;

// ----------------------------------------------------------------------------
// Union (innerer payload — der Diskriminator)
// ----------------------------------------------------------------------------
export const DimensionPayloadSchema = z.discriminatedUnion('dimension', [
  IdentityDataSchema,
  ActivityDataSchema,
  StyleDataSchema,
  ResourcesDataSchema,
  HealthDataSchema,
  PrivateDataSchema,
]);
export type DimensionPayload = z.infer<typeof DimensionPayloadSchema>;

// ----------------------------------------------------------------------------
// Persistenz-Wrapper (was in `dimension_data` Tabelle landet)
// ----------------------------------------------------------------------------
export const DimensionDataSchema = z.object({
  id: DimensionDataIdSchema,
  caseId: CaseIdSchema,
  payload: DimensionPayloadSchema,
  updatedAt: z.coerce.date(),
});
export type DimensionData = z.infer<typeof DimensionDataSchema>;
